/**
 * Дзвіночок верхньої панелі (NTF-13, NTF-18; layout.md, розд. 1). У першому екрані — лише кнопка:
 * іконка, доступна назва, `G` `N` і місце під число (без зсуву макета). Решта — лінивий чанк
 * `BellLive.tsx` (бюджет першого екрана, performance-budget.md, розд. 3): підписка на події, число,
 * «(N)» у заголовку вкладки, панель і тости. Він вантажиться на межі першого екрана
 * (`afterFirstScreen`), тож число з'являється одразу після неї, як і раніше (запит числа й так
 * чекав межі). Клік чи `G` `N` раніше межі вантажать чанк одразу й відкривають панель, щойно він
 * прийшов; обрив — наступна дія людини пробує ще раз.
 */
import { useEffect, useRef, useState } from 'react';
import { useHotkeys } from '../../app/hotkeys.ts';
import { GLOBAL } from '../../app/keymap.ts';
import { shell as t } from '../../i18n/uk/shell.ts';
import { afterFirstScreen } from '../../perf/first-screen.ts';
import { Icon } from '../../ui/Icon.tsx';
import type { BellState } from './BellLive.tsx';
// Шар панелі — у першому екрані: каркас панелі стоїть на ньому, поки вантажиться її чанк.
import './notifications.css';

const loadLive = () => import('./BellLive.tsx');
type Live = Awaited<ReturnType<typeof loadLive>>;
/** Що зробити, щойно жива частина є: відкрити панель, перемкнути її, підтягнути її код. */
type Act = 'openCenter' | 'toggleCenter' | 'preloadPanel';

export function NotificationBell() {
  const [live, setLive] = useState<Live>();
  // До живої частини числа ще не відомо: назва без нього.
  const [view, setView] = useState<BellState>();
  const bell = useRef<HTMLButtonElement>(null);
  // Модуль уже прийшов — `import()` віддає його одразу; обрив — наступна дія людини пробує ще раз.
  const run = (act?: Act) =>
    void loadLive().then(
      (module) => {
        setLive(module);
        if (act) module[act]();
      },
      () => {},
    );

  // biome-ignore lint/correctness/useExhaustiveDependencies: `run` лише вантажить модуль і ставить стан
  useEffect(() => afterFirstScreen(() => run()), []);

  useHotkeys(
    [
      {
        ...GLOBAL.goNotifications,
        label: t.notificationsKey,
        handler: () => run('openCenter'),
        command: { id: 'nav.notifications', group: 'nav' },
      },
    ],
    { scope: 'global' },
  );

  const Live = live?.NotificationLive;
  return (
    <>
      <button
        ref={bell}
        className="btn btn--ghost btn--icon topbar__bell"
        type="button"
        aria-label={view?.label ?? t.notifications}
        aria-haspopup="dialog"
        aria-expanded={Boolean(view?.controls)}
        aria-controls={view?.controls}
        title={`${t.notifications} (G N)`}
        onClick={() => run('toggleCenter')}
        // Перший клік по іконці підтягує код панелі ще до відпускання кнопки.
        onPointerEnter={() => run('preloadPanel')}
        data-testid="notifications-bell"
      >
        <Icon name="bell" />
        {view?.count ? (
          <span
            className="count count--accent"
            aria-hidden="true"
            data-testid="notifications-count"
          >
            {view.count}
          </span>
        ) : null}
      </button>
      {Live && <Live bell={bell} onState={setView} />}
    </>
  );
}
